import State from 'store/State';
import { useDispatch, useSelector } from 'react-redux';
import { useWidgetContext } from 'widgets/WidgetContext';
import { useWidgetsService } from 'widgets/hooks';
import { BooleanKeys, OnlyBoolean } from 'utils/types/BooleanKeys';
import { UseToggleConfigOptionRet } from 'widgets/store/configuration/configSlice';
import {
    buildUpdateWidgetConfigurationActionCreator,
    createLoadWidgetConfigurationAction,
} from 'widgets/store/configuration/actions';
import { useCallback } from 'react';
import { selectWidgetConfigState } from 'widgets/store/selectors';
import { WidgetType } from 'widgets/WidgetType';

export const useDispatchUpdateWidgetConfiguration = <T>() => {
    const dispatch = useDispatch();
    const { widgetType, widgetKey } = useWidgetContext();
    const updateWidgetConfigurationAction =
        buildUpdateWidgetConfigurationActionCreator<T>(widgetType);
    return useCallback(
        (configDelta: Partial<T>, replace?: boolean) =>
            dispatch(
                updateWidgetConfigurationAction(widgetKey, configDelta, replace)
            ),
        [dispatch, updateWidgetConfigurationAction, widgetKey]
    );
};

export const useDispatchLoadWidgetConfiguration = <T>() => {
    const dispatch = useDispatch();
    const { widgetType, widgetKey } = useWidgetContext();
    const actionBuilder = createLoadWidgetConfigurationAction(widgetType);
    return useCallback(
        (configDelta: Partial<T>) =>
            dispatch(actionBuilder(widgetKey, configDelta)),
        [dispatch, actionBuilder, widgetKey]
    );
};

export const useDispatchUpdateWidgetConfigurationWithoutContext = <T>(
    widgetType: WidgetType,
    widgetKey: string
) => {
    const dispatch = useDispatch();
    const updateWidgetConfigurationAction =
        buildUpdateWidgetConfigurationActionCreator<T>(widgetType);
    return useCallback(
        (configDelta: Partial<T>) =>
            dispatch(updateWidgetConfigurationAction(widgetKey, configDelta)),
        [dispatch, updateWidgetConfigurationAction, widgetKey]
    );
};

export const useSelectWidgetConfigState = <TConfigState>(
    widgetType: WidgetType,
    widgetKey: string
) => {
    const widgetsService = useWidgetsService();
    return useSelector(
        (state: State) =>
            selectWidgetConfigState(
                state,
                {
                    id: widgetKey,
                    type: widgetType,
                },
                widgetsService
            ) as TConfigState
    );
};

export const useWidgetConfigState = <TConfigState>() => {
    const { widgetType, widgetKey } = useWidgetContext();
    return useSelectWidgetConfigState<TConfigState>(widgetType, widgetKey);
};

export const useToggleConfigOption = <TConfigState>(
    option?: BooleanKeys<TConfigState>
): UseToggleConfigOptionRet => {
    const configState = useWidgetConfigState<TConfigState>();
    const updateConfig = useDispatchUpdateWidgetConfiguration<TConfigState>();

    if (!option) {
        return {
            value: false,
            toggle: () => {
                return;
            },
        };
    }
    const value = !!configState[option];
    const toggle = () => {
        const delta: Partial<OnlyBoolean<TConfigState>> = {};
        delta[option] = !value;
        updateConfig(delta as Partial<TConfigState>);
    };

    return { value, toggle };
};
